// @ts-check

/**
 * @typedef {Object} PadVisit
 * @property {number} id
 * @property {number} totalAmount
 * @property {PadPrescription[]} padPrescriptions // Opção: array de prescrições associadas a este PAD Visit. Opcional.
 */

/** @typedef {import('./buildPadPrescriptionEvent').DtEvent} DtEvent */
/** @typedef {import('./buildPadPrescriptionEvent').PadPrescription} PadPrescription */

import DateFns from 'date-fns';
import { applyGroupEventsByTimeGapRules } from './applyGroupEventsByTimeGapRules';
import { applyScheduleDisplayRules } from './applyScheduleDisplayRules';

/**
 * Formata uma data para o padrão DD/MM/YYYY
 * @param {Date} date - Objeto Date
 * @returns {string} - Data formatada
 */
const formatDateToDDMMYYYY = date => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Formata uma data para o padrão HH:mm
 * @param {Date} date - Objeto Date
 * @returns {string} - Hora formatada
 */
const formatTimeToHHMM = date => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Transforma prescriptionCalendars em um agendamento de visita
 * @param {import('./buildPadPrescriptionEvent').PadPrescription[]} padPrescriptions Array de prescrições
 * @param {PadVisit} padVisit Objeto da visita
 * @returns {Array<Object>} Array de itens de agendamento de visita
 */
export const transformPadPrescriptionsToPrescriptionLoadSchedule = (padPrescriptions, padVisit) => {
  const { totalAmount } = padVisit;

  /** @type {DtEvent[]} */
  const allDateTimePrescriptionEvents = [];

  padPrescriptions.forEach(padPrescription => {
    const { id: padPrescriptionId, prescriptionContent = {} } = padPrescription;
    const { prescriptionCalendars = [] } = prescriptionContent;

    prescriptionCalendars
      .filter(({ administrateDate }) => DateFns.isValid(new Date(administrateDate)))
      .filter(({ administrateVisitationId }) => !administrateVisitationId)
      .forEach(prescriptionCalendar => {
        const { administrateDate } = prescriptionCalendar;
        const administrateDateDateTime = new Date(administrateDate);
        const date = formatDateToDDMMYYYY(administrateDateDateTime);
        const time = formatTimeToHHMM(administrateDateDateTime);

        allDateTimePrescriptionEvents.push({ date, time, padPrescriptionId, padPrescription });
      });
  });

  // 2. Agrupa os eventos pelo gap de tempo
  const scheduleMap = applyGroupEventsByTimeGapRules(allDateTimePrescriptionEvents);

  const scheduleList = Array.from(scheduleMap.values());

  // 3. Aplica as regras de negócio de exibição/limites e retorna
  const transformedSchedule = applyScheduleDisplayRules(scheduleList, totalAmount);

  return transformedSchedule;
};
